import React from "react";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import Stack from "@mui/material/Stack";
import usePayeeCurbpassSettings from "@app.automotus.io/components/hooks/usePayeeCurbpassSettings";
import { formatCurrency } from "common/format";
import Typography from "@mui/material/Typography";
import isBefore from "date-fns/isBefore";
import LoadingSkeleton from "@app.automotus.io/components/LoadingSkeleton";

export const PaymentPreferenceSelect: React.FC<PaymentPreferenceSelectProps> = ({
  selectedPaymentPreference,
  onChangePaymentPreference,
}) => {
  const { curbPassSettings, loading: curbPassSettingsLoading } = usePayeeCurbpassSettings();

  if (curbPassSettingsLoading) {
    return <LoadingSkeleton width={"100%"} />;
  }

  const availableOptions = [];
  if (
    curbPassSettings &&
    curbPassSettings.walletPaymentEnabledAt &&
    isBefore(new Date(curbPassSettings.walletPaymentEnabledAt), new Date())
  ) {
    availableOptions.push("wallet_balance");
  }
  if (
    curbPassSettings &&
    curbPassSettings.sessionPaymentEnabledAt &&
    isBefore(new Date(curbPassSettings.sessionPaymentEnabledAt), new Date())
  ) {
    availableOptions.push("session");
  }

  const handleChangePaymentPreference = (event: React.MouseEvent<HTMLElement>, value: string) => {
    onChangePaymentPreference(value as "wallet_balance" | "session");
  };

  return (
    <Stack spacing={2}>
      {availableOptions.length > 1 && (
        <ToggleButtonGroup
          color="primary"
          fullWidth
          value={selectedPaymentPreference}
          exclusive
          onChange={handleChangePaymentPreference}
        >
          <ToggleButton sx={{ textTransform: "none" }} value="wallet_balance">
            Pay with Wallet
          </ToggleButton>
          <ToggleButton sx={{ textTransform: "none" }} value="session">
            Pay per Session
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      {selectedPaymentPreference === "session" && <ServiceFeeDetails />}
      {selectedPaymentPreference === "wallet_balance" && (
        <Typography variant="body2">
          We’ll auto refill your CurbPass when it gets low. You can request a full refund anytime!
        </Typography>
      )}
    </Stack>
  );
};

export interface PaymentPreferenceSelectProps {
  selectedPaymentPreference: "wallet_balance" | "session";
  onChangePaymentPreference: (paymentPreference: "wallet_balance" | "session") => void;
}

const ServiceFeeDetails: React.FC = () => {
  const { curbPassSettings, loading } = usePayeeCurbpassSettings();

  if (loading) {
    return <LoadingSkeleton width={"100%"} />;
  }

  if (
    curbPassSettings?.sessionPaymentServiceFeeBasisAmount === 0 &&
    curbPassSettings?.sessionPaymentServiceFeeRatio === 0
  ) {
    return null;
  }

  let serviceFeeDescriptionText = "A service fee of";

  if (curbPassSettings?.sessionPaymentServiceFeeBasisAmount) {
    serviceFeeDescriptionText += ` $${formatCurrency(curbPassSettings.sessionPaymentServiceFeeBasisAmount)}`;
  }

  if (curbPassSettings?.sessionPaymentServiceFeeRatio) {
    if (curbPassSettings?.sessionPaymentServiceFeeBasisAmount) {
      serviceFeeDescriptionText += " +";
    }
    serviceFeeDescriptionText += ` ${(curbPassSettings.sessionPaymentServiceFeeRatio * 100).toFixed(0)}%`;
  }

  serviceFeeDescriptionText += " will be charged in addition to each parking payment.";

  return <Typography variant="body2">{serviceFeeDescriptionText}</Typography>;
};

export default PaymentPreferenceSelect;
